import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { VoiceRecorder } from "capacitor-voice-recorder";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mic, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface TestResult {
  step: string;
  success: boolean;
  details: string;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

const MicTest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [audioInfo, setAudioInfo] = useState<{
    mimeType: string;
    size: number;
    duration?: number;
  } | null>(null);

  const addResult = useCallback((result: TestResult) => {
    setResults((prev) => [...prev, result]);
  }, []);

  const runTest = async () => {
    setTesting(true);
    setResults([]);
    setAudioInfo(null);

    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    addResult({
      step: "Platform Detection",
      success: true,
      details: `Platform: ${platform}, Native: ${isNative}`,
    });

    if (isNative) {
      await runNativeTest();
    } else {
      await runWebTest();
    }

    setTesting(false);
  };

  const runNativeTest = async () => {
    // Check permission
    try {
      const permResult = await VoiceRecorder.hasAudioRecordingPermission();
      let hasPermission = permResult.value;

      if (!hasPermission) {
        addResult({
          step: "Permission Check",
          success: false,
          details: "No permission, requesting...",
        });

        const reqResult = await VoiceRecorder.requestAudioRecordingPermission();
        hasPermission = reqResult.value;
      }

      addResult({
        step: "Permission",
        success: hasPermission,
        details: hasPermission ? "Granted" : "Denied",
      });

      if (!hasPermission) {
        return;
      }
    } catch (err: any) {
      addResult({
        step: "Permission",
        success: false,
        details: `Error: ${err?.message || String(err)}`,
      });
      return;
    }

    // Start recording
    try {
      await VoiceRecorder.startRecording();
      addResult({
        step: "Start Recording",
        success: true,
        details: "Recording started",
      });
    } catch (err: any) {
      addResult({
        step: "Start Recording",
        success: false,
        details: `Error: ${err?.message || String(err)}`,
      });
      return;
    }

    // Wait 2 seconds
    addResult({
      step: "Recording",
      success: true,
      details: "Recording for 2 seconds...",
    });

    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Stop recording
    try {
      const result = await VoiceRecorder.stopRecording();
      const { mimeType, msDuration, recordDataBase64 } = result.value;

      const audioBlob = base64ToBlob(recordDataBase64, mimeType);

      setAudioInfo({
        mimeType: mimeType || "(empty)",
        size: audioBlob.size,
        duration: msDuration,
      });

      addResult({
        step: "Stop Recording",
        success: true,
        details: `MIME: ${mimeType || "(empty)"}, Size: ${audioBlob.size} bytes, Duration: ${msDuration}ms`,
      });

      // Test upload
      await testUpload(audioBlob, mimeType);
    } catch (err: any) {
      addResult({
        step: "Stop Recording",
        success: false,
        details: `Error: ${err?.message || String(err)}`,
      });
    }
  };

  const runWebTest = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      addResult({
        step: "MediaDevices",
        success: false,
        details: "getUserMedia not available",
      });
      return;
    }

    let stream: MediaStream | null = null;

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      addResult({
        step: "Permission",
        success: true,
        details: "Microphone access granted",
      });
    } catch (err: any) {
      addResult({
        step: "Permission",
        success: false,
        details: `Error: ${err?.name || err?.message || String(err)}`,
      });
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
      ? "audio/webm"
      : "audio/mp4";

    addResult({
      step: "MIME Type",
      success: true,
      details: `Using: ${mimeType}`,
    });

    const chunks: Blob[] = [];
    const mediaRecorder = new MediaRecorder(stream, { mimeType });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const recordingPromise = new Promise<Blob>((resolve) => {
      mediaRecorder.onstop = () => {
        stream?.getTracks().forEach((track) => track.stop());
        const audioBlob = new Blob(chunks, { type: mimeType });
        resolve(audioBlob);
      };
    });

    mediaRecorder.start();
    addResult({
      step: "Recording",
      success: true,
      details: "Recording for 2 seconds...",
    });

    await new Promise((resolve) => setTimeout(resolve, 2000));
    mediaRecorder.stop();

    const audioBlob = await recordingPromise;

    setAudioInfo({
      mimeType,
      size: audioBlob.size,
    });

    addResult({
      step: "Stop Recording",
      success: true,
      details: `MIME: ${mimeType}, Size: ${audioBlob.size} bytes`,
    });

    await testUpload(audioBlob, mimeType);
  };

  const testUpload = async (audioBlob: Blob, mimeType: string) => {
    if (!user) {
      addResult({
        step: "Upload Test",
        success: false,
        details: "Not authenticated - cannot test upload",
      });
      return;
    }

    const effectiveMime = (mimeType || "audio/aac").split(";")[0];
    const extension = effectiveMime.includes("aac")
      ? "aac"
      : effectiveMime.includes("m4a")
      ? "m4a"
      : effectiveMime.includes("webm")
      ? "webm"
      : "bin";

    const testFileName = `mic-test/${user.id}/${Date.now()}.${extension}`;

    try {
      const { data, error } = await supabase.storage
        .from("chat-attachments")
        .upload(testFileName, audioBlob, {
          cacheControl: "60",
          upsert: true,
          contentType: effectiveMime,
        });

      if (error) {
        addResult({
          step: "Upload Test",
          success: false,
          details: `Error: ${error.message}`,
        });
      } else {
        addResult({
          step: "Upload Test",
          success: true,
          details: `Uploaded to: ${data.path}`,
        });

        // Cleanup test file
        await supabase.storage.from("chat-attachments").remove([testFileName]);
        addResult({
          step: "Cleanup",
          success: true,
          details: "Test file removed",
        });
      }
    } catch (err: any) {
      addResult({
        step: "Upload Test",
        success: false,
        details: `Exception: ${err?.message || String(err)}`,
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 pb-20 overflow-y-auto">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Microphone Test</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mic className="h-5 w-5" />
              Voice Recording Debug
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This test will record 2 seconds of audio and display detailed
              information about the recording format and upload process.
            </p>

            <Button
              onClick={runTest}
              disabled={testing}
              className="w-full"
              size="lg"
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4 mr-2" />
                  Run 2-Second Mic Test
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {audioInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Audio Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 font-mono text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">MIME Type:</span>
                <span className="font-semibold">{audioInfo.mimeType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Size:</span>
                <span className="font-semibold">
                  {(audioInfo.size / 1024).toFixed(2)} KB
                </span>
              </div>
              {audioInfo.duration !== undefined && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="font-semibold">{audioInfo.duration} ms</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Test Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {results.map((result, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 text-sm border-b last:border-0 pb-2 last:pb-0"
                >
                  {result.success ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium">{result.step}</div>
                    <div className="text-muted-foreground break-all">
                      {result.details}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MicTest;
